import { type ComponentType, lazy } from 'react'

import AppRoot from '../pages/app/[...]'

// Route configuration interface
export interface RouteConfig {
  path: string
  // biome-ignore lint/suspicious/noExplicitAny: generic route components accept any props
  component: React.LazyExoticComponent<ComponentType<any>> | ComponentType<any>
  eager?: boolean
  exact?: boolean
  children?: RouteConfig[]
}

/**
 * Complete route configuration for React Router
 */
export const routes: RouteConfig[] = [
  // Static Routes
  {
    path: '/',
    component: lazy(() => import('../pages/index')),
    exact: true,
  },
  {
    path: '/404',
    component: lazy(() => import('../pages/404')),
  },

  // Auth Callback Routes
  {
    path: '/oidc-callback',
    component: lazy(() => import('../pages/oidc-callback')),
  },
  {
    path: '/silent-oidc-callback',
    component: lazy(() => import('../pages/silent-oidc-callback')),
  },

  // App Routes - Admin
  {
    path: '/app/admin/search-jobs',
    component: lazy(() => import('../pages/app/admin/search-jobs')),
  },

  // App Routes - Assets
  {
    path: '/app/assets/analysis',
    component: lazy(() => import('../pages/app/assets/analysis')),
  },
  {
    path: '/app/assets/table',
    component: lazy(() => import('../pages/app/assets/table')),
  },

  // App Routes - Config
  {
    path: '/app/config/dcs',
    component: lazy(() => import('../pages/app/config/dcs')),
  },
  {
    path: '/app/config/periodic-jobs',
    component: lazy(() => import('../pages/app/config/periodic-jobs')),
    exact: true,
  },
  {
    path: '/app/config/periodic-jobs/new',
    component: lazy(() => import('../pages/app/config/periodic-jobs/new')),
  },

  // Dynamic Periodic Jobs Routes - Order matters! More specific routes first
  {
    path: '/app/config/periodic-jobs/:id/violations',
    component: lazy(() => import('../pages/app/config/periodic-jobs/[id]/violations')),
  },
  {
    path: '/app/config/periodic-jobs/:id/edit',
    component: lazy(() => import('../pages/app/config/periodic-jobs/[id]/edit')),
  },
  {
    path: '/app/config/periodic-jobs/:id',
    component: lazy(() => import('../pages/app/config/periodic-jobs/[id]')),
  },

  // App Routes - Documents
  {
    path: '/app/documents',
    component: lazy(() => import('../pages/app/documents')),
  },

  // App Routes - Group (Dynamic)
  {
    path: '/app/group/:groupId',
    component: lazy(() => import('../pages/app/group/[groupId]')),
  },

  // App Routes - IOW Violations
  {
    path: '/app/iow-violations',
    component: lazy(() => import('../pages/app/iow-violations')),
  },

  // App Routes - Jobs
  {
    path: '/app/jobs',
    component: lazy(() => import('../pages/app/jobs')),
  },

  // Bundlesight Routes
  {
    path: '/bundlesight/dashboard',
    component: lazy(() => import('../pages/bundlesight/dashboard')),
  },
  {
    path: '/bundlesight/datatable',
    component: lazy(() => import('../pages/bundlesight/datatable')),
  },

  // Legal Routes
  {
    path: '/legal/open-source-disclosure',
    component: lazy(() => import('../pages/legal/open-source-disclosure')),
  },

  // Support Routes
  {
    path: '/support/feedback',
    component: lazy(() => import('../pages/support/feedback')),
  },
  {
    path: '/support/help',
    component: lazy(() => import('../pages/support/help')),
  },
  {
    path: '/support/request-trial',
    component: lazy(() => import('../pages/support/request-trial')),
  },

  // Catch-all Routes (Must be last!)
  {
    path: '/app/dashboards/*',
    component: lazy(() => import('../pages/app/dashboards/[...id]')),
  },
  {
    path: '/app/*',
    component: AppRoot,
    eager: true,
  },
]

/**
 * Calculator routes - will be dynamically generated
 * Pattern: /app/calculator/:calcId
 * This will be handled separately by the calculator-routes.ts file
 */
export const calculatorRoutePattern = '/app/calculator/:calcId'
